<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import {
  getUnixTime,
  startOfDay,
  endOfDay,
  startOfWeek,
  startOfMonth,
} from 'date-fns';
import BarChart from 'shared/components/charts/BarChart.vue';
// GK-HUB: IspChartjsLine for sparkline (smooth curves + gradient fill)
import IspChartjsLine from './components/IspChartjsLine.vue';
import { formatTime, trendLabel } from './composables/ispFormatters';
// GK-HUB: Import role helper for team filter visibility
import { getUserRole } from 'dashboard/helper/permissionsHelper';

const store = useStore();
const { t } = useI18n();

// ── Period selection ──
const selectedRange = ref('month');
const from = ref(getUnixTime(startOfMonth(new Date())));
const to = ref(getUnixTime(endOfDay(new Date())));

// GK-HUB: Team filter for admin/manager (shows same names as "Times" tab)
const currentUser = computed(() => store.getters.getCurrentUser);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const userRole = computed(() =>
  getUserRole(currentUser.value, currentAccountId.value)
);
const showTeamFilter = computed(() =>
  ['administrator', 'manager'].includes(userRole.value)
);
const teams = computed(() => store.getters['teams/getTeams']);
const selectedTeamId = ref('');
// GK-HUB: end team filter setup

// ── Data from store ──
const painel = computed(() => store.getters['ispReports/getPainel']);
const kpis = computed(() => painel.value.data?.kpis || {});
const sparkline = computed(() => painel.value.data?.sparkline || []);
const topMotivos = computed(() => painel.value.data?.top_motivos || []);
const agentesOnline = computed(() => painel.value.data?.agentes_online || []);
const volumeHora = computed(() => painel.value.data?.volume_hora || []);
const painelError = computed(() => painel.value.error);

// ── Fetch ──
const fetchData = () => {
  const params = { from: from.value, to: to.value };
  // GK-HUB: Pass team_id if selected (matches "Times" tab names)
  if (selectedTeamId.value) params.teamId = selectedTeamId.value;
  store.dispatch('ispReports/fetchPainel', params);
};

const setRange = val => {
  selectedRange.value = val;
  const now = new Date();
  if (val === 'today') {
    from.value = getUnixTime(startOfDay(now));
  } else if (val === 'week') {
    from.value = getUnixTime(startOfWeek(now, { weekStartsOn: 1 }));
  } else {
    from.value = getUnixTime(startOfMonth(now));
  }
  to.value = getUnixTime(endOfDay(now));
  fetchData();
};

// GK-HUB: Auto-refresh every 30s for near real-time KPIs (fila, agentes, volume)
// Backend cache is also 30s — data updates within 30s of any change
const LIVE_POLL_MS = 30000;
let refreshInterval = null;
onMounted(() => {
  fetchData();
  // GK-HUB: Fetch teams for dropdown (same names as "Times" tab)
  if (showTeamFilter.value) store.dispatch('teams/get');
  refreshInterval = setInterval(fetchData, LIVE_POLL_MS);
});
onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval);
});

// GK-HUB: Re-fetch when team filter changes
watch(selectedTeamId, fetchData);

const retry = fetchData;

// ── Chart.js series for atendimentos/dia (IspChartjsLine) ──
const formatDate = iso => {
  if (!iso) return '';
  const parts = iso.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : iso;
};

// GK-HUB: Replaced BarChart with IspChartjsLine (smooth curves + gradient fill)
const chartSeries = computed(() => {
  const data = sparkline.value;
  if (!data.length) return [];
  const useShortLabels = data.length <= 7;
  return [
    {
      name: t('ISP_REPORTS.PAINEL.ATENDIMENTOS'),
      color: '#2E86C1',
      data: data.map(d => ({
        label: useShortLabels ? d.day_name : formatDate(d.date),
        value: d.count,
      })),
    },
  ];
});

// ── Top Motivos ──
const motivoMaxCount = computed(() =>
  Math.max(...topMotivos.value.map(m => m.count), 1)
);

const motivoBarGradient = idx => {
  const gradients = [
    'linear-gradient(90deg, #c0392b, #EF4444)',
    'linear-gradient(135deg, #1B6B93, #5DADE2)',
    'linear-gradient(90deg, #b7770d, #F59E0B)',
    'linear-gradient(135deg, #1B6B93, #5DADE2)',
    'linear-gradient(135deg, #1B6B93, #5DADE2)',
  ];
  return gradients[idx] || gradients[1];
};

// ── Agentes ──
const getInitials = name => {
  if (!name) return '??';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
};

const activeAgentCount = computed(
  () =>
    agentesOnline.value.filter(
      a => a.availability === 'online' || a.availability === 'busy'
    ).length
);

// ── Volume por hora (Chart.js) ──
const volumeChartCollection = computed(() => {
  const data = volumeHora.value;
  const peak = data.reduce((max, h) => (h.count > max.count ? h : max), {
    count: 0,
  });
  return {
    labels: data.map(h => h.label),
    datasets: [
      {
        data: data.map(h => h.count),
        backgroundColor: data.map(h =>
          h.hour === peak.hour && h.count > 0
            ? 'rgba(46,134,193,0.9)'
            : 'rgba(27,107,147,0.6)'
        ),
        hoverBackgroundColor: 'rgba(93,173,226,0.9)',
        borderRadius: 3,
        maxBarThickness: 24,
      },
    ],
  };
});

const volumeChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1A2332',
      titleColor: '#F0F2F7',
      bodyColor: '#8E97AD',
      borderColor: '#2A3650',
      borderWidth: 1,
      cornerRadius: 6,
      padding: 8,
    },
  },
  animation: { duration: 0 },
  scales: {
    x: {
      ticks: {
        color: '#5A6478',
        font: { size: 10, family: 'Inter' },
        autoSkip: true,
        maxTicksLimit: 8,
      },
      grid: { display: false },
    },
    y: {
      ticks: {
        color: '#5A6478',
        font: { size: 10, family: 'Inter' },
        precision: 0,
        beginAtZero: true,
      },
      grid: { color: 'rgba(42,54,80,0.3)', drawBorder: false },
    },
  },
}));

// ── Trend helpers ──
const trendDir = (value, invertedBetter = false) => {
  if (!value || value === 0) return 'neutral';
  if (invertedBetter) return value < 0 ? 'up' : 'down';
  return value > 0 ? 'up' : 'down';
};

const trendArrow = dir => {
  if (dir === 'up') return '\u2191';
  if (dir === 'down') return '\u2193';
  return '';
};

const trendClass = dir => {
  if (dir === 'up') return 'isp-trend-up';
  if (dir === 'down') return 'isp-trend-down';
  return 'isp-trend-neutral';
};

// ── Period labels ──
const periodOptions = computed(() => [
  { val: 'today', label: t('ISP_REPORTS.PAINEL.HOJE') },
  { val: 'week', label: t('ISP_REPORTS.PAINEL.ESTA_SEMANA') },
  { val: 'month', label: t('ISP_REPORTS.PAINEL.ESTE_MES') },
]);

// GK-HUB: sparklineSubtitle removed — title/subtitle now in isp-card-header instead of IspChartjsLine props
</script>

<template>
  <div class="isp-painel">
    <!-- ══ Header ══ -->
    <!-- GK-HUB: Standardized header (same pattern as IspFcr/IspConversas/IspMotivos) -->
    <div class="px-6 pt-6">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.PAINEL.HEADER') }}
      </h1>
      <p class="text-[13px] text-n-slate-10 mt-0.5">
        {{ t('ISP_REPORTS.PAINEL.SUBTITLE') }}
      </p>
    </div>

    <!-- GK-HUB: Standardized filter bar (period tabs + team select + live badge) -->
    <div class="flex items-center gap-3 mb-4 flex-wrap px-6 pt-3">
      <div class="isp-period-tabs">
        <div
          v-for="opt in periodOptions"
          :key="opt.val"
          class="isp-period-tab"
          :class="{ active: selectedRange === opt.val }"
          @click="setRange(opt.val)"
        >
          {{ opt.label }}
        </div>
      </div>
      <!-- GK-HUB: Team filter (admin/manager only) — isp-select-wrap pattern -->
      <div v-if="showTeamFilter" class="isp-select-wrap">
        <select v-model="selectedTeamId" class="isp-select">
          <option value="">
            {{ t('ISP_REPORTS.PAINEL.TODOS_TIMES') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- GK-HUB: end team filter -->
      <div class="isp-live-badge ml-auto">
        <span class="isp-live-dot" />
        {{ t('ISP_REPORTS.PAINEL.AO_VIVO') }}
      </div>
    </div>
    <!-- GK-HUB: end standardized header/filters -->

    <!-- ══ Loading ══ -->
    <div v-if="painel.isFetching && !painel.data" class="isp-loading">
      <woot-loading-state :message="t('ISP_REPORTS.LOADING')" class="h-64" />
    </div>

    <!-- ══ Error ══ -->
    <div v-else-if="painelError && !painel.data" class="isp-error">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ERROR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ERROR_MESSAGE') }}
      </p>
      <button class="isp-btn-retry" @click="retry">
        {{ t('ISP_REPORTS.RETRY') }}
      </button>
    </div>

    <template v-if="painel.data">
      <!-- ══ KPI Grid (8 cards) ══ -->
      <div class="isp-kpi-grid">
        <!-- 1. Atendimentos (total no período) -->
        <div class="isp-kpi-card">
          <div class="isp-kpi-label">
            {{ t('ISP_REPORTS.PAINEL.ATENDIMENTOS') }}
          </div>
          <div class="isp-kpi-value">{{ kpis.atendimentos_total || 0 }}</div>
          <div
            class="isp-kpi-delta"
            :class="trendClass(trendDir(kpis.atendimentos_trend))"
          >
            {{ trendArrow(trendDir(kpis.atendimentos_trend)) }}
            {{ kpis.atendimentos_dia || 0 }}/{{ t('ISP_REPORTS.PAINEL.DIA') }} ·
            {{ trendLabel(kpis.atendimentos_trend, '%') }}
            {{ t('ISP_REPORTS.PAINEL.VS_ANTERIOR') }}
          </div>
        </div>

        <!-- 2. TMA -->
        <div class="isp-kpi-card">
          <div class="isp-kpi-label">{{ t('ISP_REPORTS.PAINEL.TMA') }}</div>
          <div class="isp-kpi-value c-cyan">
            {{ formatTime(kpis.tma_seconds) }}
          </div>
          <div
            class="isp-kpi-delta"
            :class="trendClass(trendDir(kpis.tma_trend, true))"
          >
            {{ trendArrow(trendDir(kpis.tma_trend, true)) }}
            {{
              kpis.tma_trend <= 0
                ? t('ISP_REPORTS.PAINEL.MELHOROU')
                : t('ISP_REPORTS.PAINEL.PIOROU')
            }}
          </div>
        </div>

        <!-- 3. TME -->
        <div class="isp-kpi-card">
          <div class="isp-kpi-label">{{ t('ISP_REPORTS.PAINEL.TME') }}</div>
          <div class="isp-kpi-value">{{ formatTime(kpis.tme_seconds) }}</div>
          <div
            class="isp-kpi-delta"
            :class="trendClass(trendDir(kpis.tme_trend, true))"
          >
            {{ trendArrow(trendDir(kpis.tme_trend, true)) }}
            {{
              kpis.tme_trend <= 0
                ? t('ISP_REPORTS.PAINEL.MELHOROU')
                : t('ISP_REPORTS.PAINEL.PIOROU')
            }}
          </div>
        </div>

        <!-- 4. CSAT -->
        <div class="isp-kpi-card">
          <div class="isp-kpi-label">{{ t('ISP_REPORTS.PAINEL.CSAT') }}</div>
          <div class="isp-kpi-value c-success">{{ kpis.csat || 0 }}</div>
          <div
            class="isp-kpi-delta"
            :class="trendClass(trendDir(kpis.csat_trend))"
          >
            {{ trendArrow(trendDir(kpis.csat_trend)) }}
            {{ trendLabel(kpis.csat_trend) }}
          </div>
        </div>

        <!-- 5. FCR -->
        <div class="isp-kpi-card">
          <div class="isp-kpi-label">{{ t('ISP_REPORTS.PAINEL.FCR') }}</div>
          <div class="isp-kpi-value c-cyan">{{ kpis.fcr || 0 }}%</div>
          <div
            class="isp-kpi-delta"
            :class="trendClass(trendDir(kpis.fcr_trend))"
          >
            {{ trendArrow(trendDir(kpis.fcr_trend)) }}
            {{ trendLabel(kpis.fcr_trend, '%') }}
          </div>
        </div>

        <!-- 6. Abandono -->
        <div class="isp-kpi-card">
          <div class="isp-kpi-label">
            {{ t('ISP_REPORTS.PAINEL.ABANDONO') }}
          </div>
          <div class="isp-kpi-value c-error">
            {{ kpis.abandono_rate || 0 }}%
          </div>
          <div
            class="isp-kpi-delta"
            :class="trendClass(trendDir(kpis.abandono_trend, true))"
          >
            {{ trendArrow(trendDir(kpis.abandono_trend, true)) }}
            {{
              kpis.abandono_trend <= 0
                ? t('ISP_REPORTS.PAINEL.MELHOROU')
                : t('ISP_REPORTS.PAINEL.PIOROU')
            }}
          </div>
        </div>

        <!-- 7. I.A Resolução -->
        <div class="isp-kpi-card">
          <div class="isp-kpi-label">
            {{ t('ISP_REPORTS.PAINEL.IA_RESOLUTION') }}
          </div>
          <div class="isp-kpi-value c-cyan">
            {{ kpis.bot_resolution_rate || 0 }}%
          </div>
          <div
            class="isp-kpi-delta"
            :class="trendClass(trendDir(kpis.bot_trend))"
          >
            {{ trendArrow(trendDir(kpis.bot_trend)) }}
            {{ trendLabel(kpis.bot_trend, '%') }}
          </div>
        </div>

        <!-- 8. Fila Agora (Live) -->
        <div class="isp-kpi-card live">
          <div class="isp-kpi-label">
            {{ t('ISP_REPORTS.PAINEL.FILA_AGORA') }}
          </div>
          <div class="isp-kpi-value">{{ kpis.fila_atual || 0 }}</div>
          <div class="isp-kpi-delta isp-trend-neutral">
            {{ t('ISP_REPORTS.PAINEL.CONVERSAS_AGUARDANDO') }}
          </div>
        </div>
      </div>

      <!-- ══ Row: Chart (60%) + Top Motivos (40%) ══ -->
      <div class="isp-row isp-row-6-4">
        <!-- GK-HUB: IspChartjsLine for sparkline — wrapped in isp-card to match Top Motivos -->
        <div class="isp-card">
          <div class="isp-card-header">
            <div>
              <div class="isp-card-title">
                {{ t('ISP_REPORTS.PAINEL.SPARKLINE_TITLE') }}
              </div>
            </div>
          </div>
          <div v-if="sparkline.length" class="isp-sparkline-wrap">
            <IspChartjsLine
              :series="chartSeries"
              :height="170"
              :show-legend="false"
            />
          </div>
          <div v-else class="isp-no-data">
            {{ t('ISP_REPORTS.NO_DATA') }}
          </div>
        </div>

        <!-- Top Motivos -->
        <div class="isp-card">
          <div class="isp-card-header">
            <div>
              <div class="isp-card-title">
                {{ t('ISP_REPORTS.PAINEL.TOP_MOTIVOS') }}
              </div>
            </div>
          </div>
          <div class="isp-motivos-scroll">
            <div class="isp-bar-chart">
              <div
                v-for="(m, idx) in topMotivos"
                :key="m.motivo"
                class="isp-bar-row"
              >
                <span class="isp-bar-label">{{ m.motivo }}</span>
                <div class="isp-bar-track">
                  <div
                    class="isp-bar-fill"
                    :style="{
                      width: `${(m.count / motivoMaxCount) * 100}%`,
                      background: motivoBarGradient(idx),
                    }"
                  />
                </div>
                <span class="isp-bar-val">
                  {{ m.count }} &middot; {{ m.percentage }}%
                </span>
              </div>
              <div v-if="!topMotivos.length" class="isp-no-data">
                {{ t('ISP_REPORTS.NO_DATA') }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ══ Row: Agentes Online (40%) + Volume por Hora (60%) ══ -->
      <div class="isp-row isp-row-4-6">
        <!-- Agentes Online -->
        <div class="isp-card">
          <div class="isp-card-header">
            <div>
              <div class="isp-card-title">
                {{ t('ISP_REPORTS.PAINEL.AGENTES_ONLINE') }}
              </div>
              <div class="isp-card-sub isp-text-success">
                {{ activeAgentCount }} {{ t('ISP_REPORTS.PAINEL.ATIVOS') }}
              </div>
            </div>
          </div>
          <div v-if="agentesOnline.length" class="isp-agents-scroll">
            <table class="isp-data-table">
              <thead>
                <tr>
                  <th>{{ t('ISP_REPORTS.PAINEL.AGENTE') }}</th>
                  <th>{{ t('ISP_REPORTS.PAINEL.CONVS') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="agent in agentesOnline" :key="agent.id">
                  <td>
                    <div class="isp-agent-name">
                      <div v-if="agent.avatar_url" class="isp-agent-av">
                        <img
                          :src="agent.avatar_url"
                          :alt="agent.name"
                          class="isp-agent-av-img"
                        />
                      </div>
                      <div v-else class="isp-agent-av">
                        {{ getInitials(agent.name) }}
                      </div>
                      {{ agent.name }}
                    </div>
                  </td>
                  <td>{{ agent.conversations }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="isp-no-data">
            {{ t('ISP_REPORTS.NO_DATA') }}
          </div>
        </div>

        <!-- Volume por hora -->
        <div class="isp-card">
          <div class="isp-card-header">
            <div>
              <div class="isp-card-title">
                {{ t('ISP_REPORTS.PAINEL.VOLUME_HORA') }}
              </div>
              <div class="isp-card-sub">
                {{ t('ISP_REPORTS.PAINEL.PICOS_HOJE') }}
              </div>
            </div>
          </div>
          <div class="isp-chart-wrap">
            <BarChart
              v-if="volumeHora.length"
              :collection="volumeChartCollection"
              :chart-options="volumeChartOptions"
            />
            <div v-else class="isp-no-data">
              {{ t('ISP_REPORTS.NO_DATA') }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ══ Empty State ══ -->
    <div
      v-if="!painel.isFetching && !painelError && !painel.data"
      class="isp-error"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.EMPTY_PAINEL_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.EMPTY_PAINEL_MESSAGE') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* ══════════════════════════════════════
   IspPainel — GK Chat Design System v2
   ══════════════════════════════════════ */

.isp-painel {
  padding: 0;
}

/* ── Select wrapper (standardized pattern — same as IspFcr/IspMotivos) ── */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}
.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 100%;
  height: auto;
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 30px 6px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  background: none;
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
}

.isp-period-tabs {
  display: flex;
  background: #232f42;
  border: 1px solid #2a3650;
  border-radius: 6px;
  padding: 3px;
  gap: 2px;
}

.isp-period-tab {
  padding: 4px 11px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  color: #8e97ad;
  transition: all 150ms ease;
  user-select: none;
}

.isp-period-tab:hover {
  color: #f0f2f7;
}

.isp-period-tab.active {
  background: #2e86c1;
  color: #fff;
}

.isp-live-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 11px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.25);
  border-radius: 9999px;
  font-size: 11px;
  color: #10b981;
  font-weight: 500;
}

.isp-live-dot {
  width: 6px;
  height: 6px;
  background: #10b981;
  border-radius: 50%;
  animation: isp-pulse 2s infinite;
}

@keyframes isp-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.3);
  }
}

/* ── Content area ── */
.isp-loading,
.isp-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 256px;
  gap: 12px;
  padding: 20px 24px;
}

.isp-btn-retry {
  background: linear-gradient(135deg, #1b6b93, #5dade2);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 16px;
  cursor: pointer;
  font-family:
    Inter,
    -apple-system,
    sans-serif;
}

/* ── KPI Grid ── */
.isp-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
  gap: 11px;
  padding: 20px 24px 0;
  margin-bottom: 16px;
}

.isp-kpi-card {
  background: #1a2332;
  border: 1px solid #2a3650;
  border-radius: 10px;
  padding: 14px 16px;
  position: relative;
  overflow: hidden;
  transition:
    border-color 150ms ease,
    box-shadow 150ms ease;
}

.isp-kpi-card:hover {
  border-color: #3d4f6a;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.isp-kpi-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(135deg, #1b6b93, #5dade2);
  opacity: 0;
  transition: opacity 150ms ease;
}

.isp-kpi-card:hover::after {
  opacity: 1;
}

.isp-kpi-card.live .isp-kpi-label::after {
  content: '  \25CF';
  color: #10b981;
  animation: isp-pulse 2s infinite;
}

.isp-kpi-label {
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #5a6478;
  font-weight: 600;
  margin-bottom: 7px;
}

.isp-kpi-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1;
  color: #f0f2f7;
  margin-bottom: 5px;
}

.isp-kpi-value.c-cyan {
  color: #5dade2;
}

.isp-kpi-value.c-success {
  color: #10b981;
}

.isp-kpi-value.c-error {
  color: #ef4444;
}

.isp-kpi-value.c-warning {
  color: #f59e0b;
}

.isp-kpi-delta {
  font-size: 11px;
  font-weight: 500;
}

.isp-trend-up {
  color: #10b981;
}

.isp-trend-down {
  color: #ef4444;
}

.isp-trend-neutral {
  color: #5a6478;
}

/* ── Layout rows ── */
.isp-row {
  display: grid;
  gap: 13px;
  padding: 0 24px;
  margin-bottom: 13px;
}

.isp-row-6-4 {
  grid-template-columns: 6fr 4fr;
}

.isp-row-4-6 {
  grid-template-columns: 4fr 6fr;
}

/* ── Card ── */
.isp-card {
  background: #1a2332;
  border: 1px solid #2a3650;
  border-radius: 10px;
  padding: 16px 18px;
}

.isp-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
}

.isp-card-title {
  font-size: 13px;
  font-weight: 600;
  color: #f0f2f7;
}

.isp-card-sub {
  font-size: 11px;
  color: #5a6478;
  margin-top: 2px;
}

/* ── IspChartjsLine wrapper — neutralize component's own card/margins inside isp-card ── */
.isp-sparkline-wrap :deep(> div) {
  margin-bottom: 0;
}
.isp-sparkline-wrap :deep(.bg-n-solid-2) {
  background: none;
  border: none;
  padding: 0;
  border-radius: 0;
}

/* ── Motivos scroll wrapper (matches agent scroll height) ── */
.isp-motivos-scroll {
  max-height: 220px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #2a3650 transparent;
}

.isp-motivos-scroll::-webkit-scrollbar {
  width: 4px;
}

.isp-motivos-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.isp-motivos-scroll::-webkit-scrollbar-thumb {
  background: #2a3650;
  border-radius: 9999px;
}

/* ── Horizontal bar chart (motivos) ── */
.isp-bar-chart {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.isp-bar-row {
  display: flex;
  align-items: center;
  gap: 9px;
}

.isp-bar-label {
  font-size: 11px;
  color: #8e97ad;
  width: 105px;
  text-align: right;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.isp-bar-track {
  flex: 1;
  height: 6px;
  background: #232f42;
  border-radius: 9999px;
  overflow: hidden;
}

.isp-bar-fill {
  height: 100%;
  border-radius: 9999px;
  transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
}

.isp-bar-val {
  font-size: 11px;
  color: #5a6478;
  width: 72px;
  text-align: right;
  flex-shrink: 0;
}

/* ── Agent scroll wrapper (max ~5 rows visible) ── */
.isp-agents-scroll {
  max-height: 260px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #2a3650 transparent;
}

.isp-agents-scroll::-webkit-scrollbar {
  width: 4px;
}

.isp-agents-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.isp-agents-scroll::-webkit-scrollbar-thumb {
  background: #2a3650;
  border-radius: 9999px;
}

/* ── Data table (agentes) ── */
.isp-data-table {
  width: 100%;
  border-collapse: collapse;
}

.isp-data-table th {
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #5a6478;
  font-weight: 600;
  padding: 0 10px 9px;
  text-align: left;
  border-bottom: 1px solid #2a3650;
}

.isp-data-table td {
  padding: 9px 10px;
  font-size: 13px;
  border-bottom: 1px solid #1f2b3d;
  color: #f0f2f7;
}

.isp-data-table tr:last-child td {
  border-bottom: none;
}

.isp-data-table tr:hover td {
  background: #2a3650;
}

.isp-agent-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.isp-agent-av {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1b6b93, #5dade2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  overflow: hidden;
}

.isp-agent-av-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.isp-s-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
}

.isp-s-online {
  background: #10b981;
}

.isp-s-busy {
  background: #f59e0b;
}

.isp-s-break {
  background: #a78bfa;
}

.isp-s-offline {
  background: #4a5268;
}

.isp-text-success {
  color: #10b981;
}

/* ── Volume por hora uses Chart.js (same as sparkline) ── */

/* ── No data ── */
.isp-no-data {
  color: #5a6478;
  font-size: 12px;
  padding: 16px 0;
  text-align: center;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .isp-row-6-4,
  .isp-row-4-6 {
    grid-template-columns: 1fr;
  }

  .isp-kpi-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
}

/* ── Light mode adjustments ── */

body:not(.dark) .isp-kpi-card,
body:not(.dark) .isp-card {
  background: #fff;
  border-color: #d8dce8;
}

body:not(.dark) .isp-kpi-card:hover,
body:not(.dark) .isp-card:hover {
  border-color: #b8bfcf;
}

body:not(.dark) .isp-kpi-value {
  color: #111827;
}

body:not(.dark) .isp-data-table td {
  color: #111827;
  border-bottom-color: #eef0f6;
}

body:not(.dark) .isp-data-table th {
  border-bottom-color: #d8dce8;
}

body:not(.dark) .isp-data-table tr:hover td {
  background: #f7f8fc;
}

body:not(.dark) .isp-card-title {
  color: #111827;
}

body:not(.dark) .isp-period-tabs {
  background: #eef0f6;
  border-color: #d8dce8;
}

body:not(.dark) .isp-period-tab {
  color: #6b7489;
}

body:not(.dark) .isp-period-tab:hover {
  color: #111827;
}

body:not(.dark) .isp-bar-track {
  background: #eef0f6;
}
</style>

<!-- GK-HUB: Dark mode overrides for standardized selects (unscoped for body.dark targeting) -->
<style>
body.dark .isp-select {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}
body.dark .isp-select-arrow {
  color: #64748b;
}
</style>
